import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Trophy, Plus, Edit, Trash2, Loader2, Medal, Award, Calendar, 
  ChevronDown, ChevronUp, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to parse date without timezone issues
const formatEventDate = (dateStr, options = {}) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    ...options
  });
};

const getMedalColor = (place) => {
  switch (place) {
    case 1: return 'text-yellow-500';
    case 2: return 'text-gray-400';
    case 3: return 'text-amber-600';
    default: return 'text-stone-400';
  }
};

const ResultsPage = () => {
  const { user, token } = useAuth();
  const [results, setResults] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedResult, setExpandedResult] = useState(null);
  
  const isPresident = user?.role === 'president';

  const [formData, setFormData] = useState({
    event_id: '',
    event_name: '',
    event_date: '',
    notes: '',
    classes: [{ class_name: '', age_category: '', results: [{ place: 1, rider_name: '', horse_name: '', points: '', time: '', notes: '' }] }]
  });

  // Inline event class editor + registration editor state
  const [showEventEditor, setShowEventEditor] = useState(false);
  const [showRegEditor, setShowRegEditor] = useState(false);
  const [eventClasses, setEventClasses] = useState([]); // editable show_classes
  const [newClassInput, setNewClassInput] = useState('');
  const [eventRegistrations, setEventRegistrations] = useState([]); // for currently selected event
  const [savingEventEdit, setSavingEventEdit] = useState(false);
  const [savingRegId, setSavingRegId] = useState(null);
  const [showAddWalkIn, setShowAddWalkIn] = useState(false);
  const [walkInForm, setWalkInForm] = useState({ participant_name: '', horse_name: '', selected_classes: [], age_category: '' });

  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/results`),
        axios.get(`${API}/events`)
      ]);
      setResults(resultsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingResult(null);
    setFormData({
      event_id: '',
      event_name: '',
      event_date: '',
      notes: '',
      classes: [{ class_name: '', age_category: '', results: [{ place: 1, rider_name: '', horse_name: '', points: '', time: '', notes: '' }] }]
    });
    setShowModal(true);
  };

  const openEditModal = (result) => {
    setEditingResult(result);
    setFormData({
      event_id: result.event_id,
      event_name: result.event_name,
      event_date: result.event_date,
      notes: result.notes || '',
      classes: result.classes.map(c => ({
        ...c,
        results: c.results.map(r => ({
          ...r,
          points: r.points || '',
          time: r.time || '',
          notes: r.notes || ''
        }))
      }))
    });
    setShowModal(true);
  };

  // Build the pre-filled placements from a fresh registrations list.
  // Splits each event class into one ClassResult per unique age_category, so age groups stay separated.
  const rebuildPrefillFromRegistrations = (event, registrations) => {
    const regsByClass = {}; // className -> [{rider, horse, age}]
    registrations.forEach(reg => {
      const horses = reg.horses && reg.horses.length > 0
        ? reg.horses
        : (reg.horse_name ? [{ horse_name: reg.horse_name, selected_classes: reg.selected_classes || [], age_category: reg.age_category }] : []);
      horses.forEach(horse => {
        (horse.selected_classes || []).forEach(className => {
          if (!regsByClass[className]) regsByClass[className] = [];
          regsByClass[className].push({
            rider_name: reg.participant_name || '',
            horse_name: horse.horse_name || '',
            age_category: (horse.age_category || '').trim()
          });
        });
      });
    });

    const buildEntryRows = (entries) => {
      if (entries.length === 0) {
        return [{ place: 1, rider_name: '', horse_name: '', points: '', time: '', notes: '' }];
      }
      return entries.map((e, idx) => ({
        place: idx + 1,
        rider_name: e.rider_name,
        horse_name: e.horse_name,
        points: '',
        time: '',
        notes: ''
      }));
    };

    const splitClassByAge = (className) => {
      const entries = regsByClass[className] || [];
      // Group entries by age_category preserving first-seen order
      const groups = new Map();
      entries.forEach(e => {
        const key = e.age_category || '';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(e);
      });

      if (groups.size === 0) {
        // No registrations -> one empty placeholder class
        return [{ class_name: className, age_category: '', results: buildEntryRows([]) }];
      }

      return Array.from(groups.entries()).map(([age, group]) => ({
        class_name: className,
        age_category: age,
        results: buildEntryRows(group)
      }));
    };

    const classList = event.event_type === 'show' && event.show_classes && event.show_classes.length > 0
      ? event.show_classes.flatMap(splitClassByAge)
      : [{ class_name: '', age_category: '', results: [{ place: 1, rider_name: '', horse_name: '', points: '', time: '', notes: '' }] }];

    return { classList, totalPrefilled: Object.values(regsByClass).reduce((sum, arr) => sum + arr.length, 0) };
  };

  const handleEventSelect = async (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;

    let registrations = [];
    try {
      const regsRes = await axios.get(`${API}/registrations/event/${eventId}`, { headers: getAuthHeader() });
      registrations = regsRes.data || [];
    } catch (err) {
      console.warn('Could not fetch registrations for pre-fill', err);
    }

    setEventRegistrations(registrations);
    setEventClasses(event.show_classes || []);
    setNewClassInput('');
    setShowEventEditor(false);
    setShowRegEditor(false);
    setShowAddWalkIn(false);

    const { classList, totalPrefilled } = rebuildPrefillFromRegistrations(event, registrations);

    setFormData({
      ...formData,
      event_id: eventId,
      event_name: event.title,
      event_date: event.date,
      classes: classList
    });

    if (totalPrefilled > 0) {
      toast.success(`Pre-filled ${totalPrefilled} entries from registrations`);
    }
  };

  const refreshEventAndRegistrations = async (eventId) => {
    try {
      const [eventRes, regsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/registrations/event/${eventId}`, { headers: getAuthHeader() })
      ]);
      const event = eventRes.data;
      const registrations = regsRes.data || [];
      setEvents(prev => prev.map(e => e.id === eventId ? event : e));
      setEventRegistrations(registrations);
      setEventClasses(event.show_classes || []);
      const { classList } = rebuildPrefillFromRegistrations(event, registrations);
      setFormData(prev => ({ ...prev, classes: classList }));
    } catch (err) {
      console.error('Refresh failed', err);
    }
  };

  // ===== Event class list editor =====
  const addClassToEvent = () => {
    const name = newClassInput.trim();
    if (!name) return;
    if (eventClasses.includes(name)) {
      toast.error('Class already exists');
      return;
    }
    setEventClasses([...eventClasses, name]);
    setNewClassInput('');
  };

  const removeClassFromEvent = (className) => {
    setEventClasses(eventClasses.filter(c => c !== className));
  };

  const saveEventClasses = async () => {
    if (!formData.event_id) return;
    setSavingEventEdit(true);
    try {
      await axios.put(`${API}/events/${formData.event_id}`, { show_classes: eventClasses }, { headers: getAuthHeader() });
      toast.success('Event class list updated');
      await refreshEventAndRegistrations(formData.event_id);
      setShowEventEditor(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update class list');
    } finally {
      setSavingEventEdit(false);
    }
  };

  // ===== Registration editor =====
  const updateRegistrationHorse = (regId, horseIdx, field, value) => {
    setEventRegistrations(prev => prev.map(reg => {
      if (reg.id !== regId) return reg;
      const horses = reg.horses && reg.horses.length > 0
        ? [...reg.horses]
        : [{ horse_name: reg.horse_name || '', selected_classes: reg.selected_classes || [], age_category: reg.age_category }];
      horses[horseIdx] = { ...horses[horseIdx], [field]: value };
      return { ...reg, horses };
    }));
  };

  const toggleRegistrationClass = (regId, horseIdx, className) => {
    setEventRegistrations(prev => prev.map(reg => {
      if (reg.id !== regId) return reg;
      const horses = reg.horses && reg.horses.length > 0
        ? reg.horses.map(h => ({ ...h, selected_classes: [...(h.selected_classes || [])] }))
        : [{ horse_name: reg.horse_name || '', selected_classes: [...(reg.selected_classes || [])], age_category: reg.age_category }];
      const current = horses[horseIdx].selected_classes || [];
      horses[horseIdx].selected_classes = current.includes(className)
        ? current.filter(c => c !== className)
        : [...current, className];
      return { ...reg, horses };
    }));
  };

  const saveRegistration = async (reg) => {
    setSavingRegId(reg.id);
    try {
      const horses = (reg.horses && reg.horses.length > 0
        ? reg.horses
        : [{ horse_name: reg.horse_name || '', selected_classes: reg.selected_classes || [], age_category: reg.age_category }]
      ).map(h => ({
        horse_name: h.horse_name || '',
        selected_classes: h.selected_classes || [],
        age_category: h.age_category || null,
      }));
      await axios.put(`${API}/registrations/${reg.id}/admin`, {
        participant_name: reg.participant_name,
        horses,
      }, { headers: getAuthHeader() });
      toast.success('Registration updated');
      await refreshEventAndRegistrations(formData.event_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update registration');
    } finally {
      setSavingRegId(null);
    }
  };

  const deleteRegistration = async (regId) => {
    if (!window.confirm('Remove this registration?')) return;
    try {
      await axios.delete(`${API}/registrations/${regId}/admin`, { headers: getAuthHeader() });
      toast.success('Registration removed');
      await refreshEventAndRegistrations(formData.event_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove registration');
    }
  };

  const toggleWalkInClass = (className) => {
    setWalkInForm(prev => {
      const current = prev.selected_classes || [];
      return {
        ...prev,
        selected_classes: current.includes(className) ? current.filter(c => c !== className) : [...current, className]
      };
    });
  };

  const submitWalkIn = async () => {
    if (!walkInForm.participant_name || !walkInForm.horse_name) {
      toast.error('Rider and horse name are required');
      return;
    }
    if (walkInForm.selected_classes.length === 0) {
      toast.error('Pick at least one class');
      return;
    }
    try {
      await axios.post(`${API}/registrations/admin`, {
        event_id: formData.event_id,
        participant_name: walkInForm.participant_name,
        phone_number: '',
        horses: [{
          horse_name: walkInForm.horse_name,
          selected_classes: walkInForm.selected_classes,
          age_category: walkInForm.age_category || null,
        }]
      }, { headers: getAuthHeader() });
      toast.success('Walk-in entry added');
      setWalkInForm({ participant_name: '', horse_name: '', selected_classes: [], age_category: '' });
      setShowAddWalkIn(false);
      await refreshEventAndRegistrations(formData.event_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add walk-in');
    }
  };

  const addClass = () => {
    setFormData({
      ...formData,
      classes: [...formData.classes, { class_name: '', age_category: '', results: [{ place: 1, rider_name: '', horse_name: '', points: '', time: '', notes: '' }] }]
    });
  };

  const removeClass = (classIndex) => {
    if (formData.classes.length > 1) {
      setFormData({
        ...formData,
        classes: formData.classes.filter((_, i) => i !== classIndex)
      });
    }
  };

  const updateClass = (classIndex, field, value) => {
    const newClasses = [...formData.classes];
    newClasses[classIndex][field] = value;
    setFormData({ ...formData, classes: newClasses });
  };

  const addResult = (classIndex) => {
    const newClasses = [...formData.classes];
    const nextPlace = newClasses[classIndex].results.length + 1;
    newClasses[classIndex].results.push({ place: nextPlace, rider_name: '', horse_name: '', points: '', time: '', notes: '' });
    setFormData({ ...formData, classes: newClasses });
  };

  const removeResult = (classIndex, resultIndex) => {
    const newClasses = [...formData.classes];
    if (newClasses[classIndex].results.length > 1) {
      newClasses[classIndex].results = newClasses[classIndex].results.filter((_, i) => i !== resultIndex);
      // Re-number places
      newClasses[classIndex].results.forEach((r, i) => r.place = i + 1);
      setFormData({ ...formData, classes: newClasses });
    }
  };

  const updateResult = (classIndex, resultIndex, field, value) => {
    const newClasses = [...formData.classes];
    newClasses[classIndex].results[resultIndex][field] = value;
    setFormData({ ...formData, classes: newClasses });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.event_name || !formData.event_date) {
      toast.error('Please fill in event name and date');
      return;
    }

    // Validate classes have at least one result with rider name
    const validClasses = formData.classes.filter(c => 
      c.class_name && c.results.some(r => r.rider_name)
    );

    if (validClasses.length === 0) {
      toast.error('Please add at least one class with results');
      return;
    }

    setSubmitting(true);
    try {
      // Parse time string into seconds for sorting. Supports "12.34", "1:23.45", "01:02:03"
      const parseTimeToSeconds = (t) => {
        if (!t || typeof t !== 'string') return Infinity;
        const trimmed = t.trim();
        if (!trimmed) return Infinity;
        if (trimmed.includes(':')) {
          const parts = trimmed.split(':').map(p => parseFloat(p));
          if (parts.some(isNaN)) return Infinity;
          return parts.reduce((acc, val) => acc * 60 + val, 0);
        }
        const num = parseFloat(trimmed);
        return isNaN(num) ? Infinity : num;
      };

      const payload = {
        event_id: formData.event_id || `manual-${Date.now()}`,
        event_name: formData.event_name,
        event_date: formData.event_date,
        notes: formData.notes || null,
        classes: validClasses.map(c => {
          const filtered = c.results.filter(r => r.rider_name);
          // Auto-sort by fastest time when any entry has a time
          const hasAnyTime = filtered.some(r => r.time && r.time.trim() !== '');
          if (hasAnyTime) {
            filtered.sort((a, b) => parseTimeToSeconds(a.time) - parseTimeToSeconds(b.time));
          }
          return {
            class_name: c.class_name,
            age_category: c.age_category || null,
            results: filtered.map((r, idx) => ({
              place: idx + 1,
              rider_name: r.rider_name,
              horse_name: r.horse_name,
              points: r.points ? parseFloat(r.points) : null,
              time: r.time || null,
              notes: r.notes || null
            }))
          };
        })
      };

      if (editingResult) {
        await axios.put(`${API}/results/${editingResult.id}`, payload, { headers: getAuthHeader() });
        toast.success('Results updated successfully');
      } else {
        await axios.post(`${API}/results`, payload, { headers: getAuthHeader() });
        toast.success('Results created successfully');
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save results');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (resultId) => {
    if (!window.confirm('Are you sure you want to delete these results?')) return;

    try {
      await axios.delete(`${API}/results/${resultId}`, { headers: getAuthHeader() });
      toast.success('Results deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete results');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO 
        title="Results & Standings"
        description="View horse show results and standings from Manitoulin Horse Enthusiasts Club events."
        url="/results"
      />
      
      {/* Header */}
      <div className="bg-primary text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-4xl font-bold mb-2">Results & Standings</h1>
              <p className="text-white/80">View results from our horse shows and events</p>
            </div>
            {isPresident && (
              <Button 
                onClick={openCreateModal}
                className="bg-white text-primary hover:bg-stone-100"
                data-testid="add-results-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Results
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Results List */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {results.length === 0 ? (
          <Card className="border-stone-200">
            <CardContent className="py-16 text-center">
              <Trophy className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-stone-700 mb-2">No Results Yet</h2>
              <p className="text-stone-500">Results from upcoming events will be posted here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-10">
            {(() => {
              // Group results by year (desc), and events within each year by date desc
              const byYear = {};
              results.forEach(r => {
                const y = (r.event_date || '').slice(0, 4) || 'Undated';
                if (!byYear[y]) byYear[y] = [];
                byYear[y].push(r);
              });
              const years = Object.keys(byYear).sort((a, b) => {
                if (a === 'Undated') return 1;
                if (b === 'Undated') return -1;
                return b.localeCompare(a);
              });
              years.forEach(y => byYear[y].sort((a, b) => (b.event_date || '').localeCompare(a.event_date || '')));

              return years.map(year => (
                <section key={year} data-testid={`results-year-${year}`}>
                  <div className="flex items-center gap-4 mb-4 sticky top-16 z-10 bg-white/95 backdrop-blur py-2 border-b border-stone-200">
                    <h2 className="font-display text-3xl font-bold text-primary">{year}</h2>
                    <span className="text-sm text-stone-500">
                      {byYear[year].length} event{byYear[year].length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-6">
                    {byYear[year].map((result) => (
                      <Card key={result.id} className="border-stone-200 overflow-hidden">
                <CardHeader 
                  className="bg-gradient-to-r from-primary to-primary/80 text-white cursor-pointer"
                  onClick={() => setExpandedResult(expandedResult === result.id ? null : result.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <Trophy className="w-6 h-6" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-display">{result.event_name}</CardTitle>
                        <p className="text-white/80 flex items-center gap-2 mt-1">
                          <Calendar className="w-4 h-4" />
                          {formatEventDate(result.event_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {isPresident && (
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-white hover:bg-white/20"
                            onClick={() => openEditModal(result)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-white hover:bg-red-500/50"
                            onClick={() => handleDelete(result.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      {expandedResult === result.id ? (
                        <ChevronUp className="w-6 h-6" />
                      ) : (
                        <ChevronDown className="w-6 h-6" />
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                {expandedResult === result.id && (
                  <CardContent className="p-0">
                    <Tabs defaultValue={result.classes[0]?.class_name || 'class-0'} className="w-full">
                      <TabsList className="w-full justify-start rounded-none border-b bg-stone-50 p-0 h-auto flex-wrap">
                        {result.classes.map((classResult, idx) => (
                          <TabsTrigger 
                            key={idx} 
                            value={classResult.class_name || `class-${idx}`}
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-white px-4 py-3"
                          >
                            {classResult.class_name}
                            {classResult.age_category && (
                              <span className="ml-2 text-xs text-stone-500">({classResult.age_category})</span>
                            )}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      
                      {result.classes.map((classResult, idx) => (
                        <TabsContent 
                          key={idx} 
                          value={classResult.class_name || `class-${idx}`}
                          className="p-4 mt-0"
                        >
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-stone-200">
                                <th className="text-left p-3 w-16">Place</th>
                                <th className="text-left p-3">Rider</th>
                                <th className="text-left p-3">Horse</th>
                                {classResult.results.some(r => r.points) && (
                                  <th className="text-left p-3">Points</th>
                                )}
                                {classResult.results.some(r => r.time) && (
                                  <th className="text-left p-3">Time</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {classResult.results.map((entry, entryIdx) => (
                                <tr key={entryIdx} className="border-b border-stone-100 hover:bg-stone-50">
                                  <td className="p-3">
                                    <div className="flex items-center gap-2">
                                      <Medal className={`w-5 h-5 ${getMedalColor(entry.place)}`} />
                                      <span className="font-bold">{entry.place}</span>
                                    </div>
                                  </td>
                                  <td className="p-3 font-medium">{entry.rider_name}</td>
                                  <td className="p-3 text-stone-600">{entry.horse_name}</td>
                                  {classResult.results.some(r => r.points) && (
                                    <td className="p-3 font-mono">{entry.points || '-'}</td>
                                  )}
                                  {classResult.results.some(r => r.time) && (
                                    <td className="p-3 font-mono">{entry.time || '-'}</td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </TabsContent>
                      ))}
                    </Tabs>
                    
                    {result.notes && (
                      <div className="px-4 pb-4">
                        <p className="text-sm text-stone-600 bg-stone-50 p-3 rounded-lg">
                          <strong>Notes:</strong> {result.notes}
                        </p>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            ))}
                  </div>
                </section>
              ));
            })()}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingResult ? 'Edit Results' : 'Add Event Results'}
            </DialogTitle>
            <DialogDescription>
              Enter the results for each class. Results will be visible to all visitors.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Event Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Select Event (Optional)</Label>
                <Select value={formData.event_id || undefined} onValueChange={handleEventSelect}>
                  <SelectTrigger className="bg-stone-50">
                    <SelectValue placeholder="Select an event..." />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map(event => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.title} ({formatEventDate(event.date, { month: 'short' })})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Event Date *</Label>
                <Input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                  className="bg-stone-50"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Event Name *</Label>
              <Input
                value={formData.event_name}
                onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                placeholder="e.g., Spring Horse Show 2025"
                className="bg-stone-50"
                required
              />
            </div>

            {/* Inline Event & Registration Editors (only when an event is selected) */}
            {formData.event_id && !formData.event_id.startsWith('manual-') && (
              <div className="space-y-3 border border-stone-200 rounded-lg p-4 bg-stone-50/40">
                {/* Event Classes Editor */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowEventEditor(!showEventEditor)}
                    className="flex items-center justify-between w-full text-left font-semibold text-stone-800 hover:text-primary"
                    data-testid="toggle-event-classes-editor"
                  >
                    <span className="flex items-center gap-2">
                      <Edit className="w-4 h-4" />
                      Edit Event Class List ({eventClasses.length})
                    </span>
                    {showEventEditor ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {showEventEditor && (
                    <div className="mt-3 space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {eventClasses.length === 0 && (
                          <span className="text-sm text-stone-500">No classes yet. Add one below.</span>
                        )}
                        {eventClasses.map(cls => (
                          <span
                            key={cls}
                            className="inline-flex items-center gap-1 bg-white border border-stone-300 rounded-full px-3 py-1 text-sm"
                            data-testid={`event-class-chip-${cls}`}
                          >
                            {cls}
                            <button
                              type="button"
                              onClick={() => removeClassFromEvent(cls)}
                              className="text-stone-500 hover:text-red-500 ml-1"
                              aria-label={`Remove ${cls}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={newClassInput}
                          onChange={(e) => setNewClassInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addClassToEvent(); } }}
                          placeholder="New class name…"
                          className="bg-white"
                          data-testid="new-class-input"
                        />
                        <Button type="button" variant="outline" onClick={addClassToEvent} data-testid="add-class-to-event-btn">
                          <Plus className="w-4 h-4 mr-1" /> Add
                        </Button>
                        <Button
                          type="button"
                          onClick={saveEventClasses}
                          disabled={savingEventEdit}
                          className="bg-primary"
                          data-testid="save-event-classes-btn"
                        >
                          {savingEventEdit && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                          Save Classes
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Registrations Editor */}
                <div className="border-t border-stone-200 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowRegEditor(!showRegEditor)}
                    className="flex items-center justify-between w-full text-left font-semibold text-stone-800 hover:text-primary"
                    data-testid="toggle-registrations-editor"
                  >
                    <span className="flex items-center gap-2">
                      <Edit className="w-4 h-4" />
                      Edit Registrations ({eventRegistrations.length})
                    </span>
                    {showRegEditor ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {showRegEditor && (
                    <div className="mt-3 space-y-3">
                      {eventRegistrations.length === 0 && (
                        <p className="text-sm text-stone-500">No registrations yet. Add a walk-in below.</p>
                      )}
                      {eventRegistrations.map(reg => {
                        const horses = reg.horses && reg.horses.length > 0
                          ? reg.horses
                          : [{ horse_name: reg.horse_name || '', selected_classes: reg.selected_classes || [], age_category: reg.age_category }];
                        return (
                          <Card key={reg.id} className="border-stone-200" data-testid={`reg-card-${reg.id}`}>
                            <CardContent className="p-3 space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-medium text-stone-800 flex items-center gap-2">
                                  {reg.participant_name}
                                  {reg.walk_in && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">walk-in</span>}
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => saveRegistration(reg)}
                                    disabled={savingRegId === reg.id}
                                    data-testid={`save-reg-${reg.id}`}
                                  >
                                    {savingRegId === reg.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteRegistration(reg.id)}
                                    className="text-red-500 hover:text-red-700"
                                    data-testid={`delete-reg-${reg.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              {horses.map((horse, hIdx) => (
                                <div key={hIdx} className="bg-stone-50 rounded p-2 space-y-2">
                                  <div className="grid grid-cols-2 gap-2">
                                    <Input
                                      value={horse.horse_name || ''}
                                      onChange={(e) => updateRegistrationHorse(reg.id, hIdx, 'horse_name', e.target.value)}
                                      placeholder="Horse name"
                                      className="bg-white"
                                    />
                                    <Input
                                      value={horse.age_category || ''}
                                      onChange={(e) => updateRegistrationHorse(reg.id, hIdx, 'age_category', e.target.value)}
                                      placeholder="Age category"
                                      className="bg-white"
                                    />
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {eventClasses.map(cls => {
                                      const selected = (horse.selected_classes || []).includes(cls);
                                      return (
                                        <button
                                          type="button"
                                          key={cls}
                                          onClick={() => toggleRegistrationClass(reg.id, hIdx, cls)}
                                          className={`text-xs px-2 py-1 rounded-full border ${selected ? 'bg-primary text-white border-primary' : 'bg-white text-stone-600 border-stone-300 hover:border-primary'}`}
                                        >
                                          {cls}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </CardContent>
                          </Card>
                        );
                      })}

                      {/* Add Walk-In */}
                      {!showAddWalkIn ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowAddWalkIn(true)}
                          className="w-full"
                          data-testid="add-walkin-btn"
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add Walk-In Rider
                        </Button>
                      ) : (
                        <Card className="border-amber-300 bg-amber-50/40">
                          <CardContent className="p-3 space-y-2">
                            <div className="font-medium text-stone-800 text-sm">New walk-in entry</div>
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                value={walkInForm.participant_name}
                                onChange={(e) => setWalkInForm({ ...walkInForm, participant_name: e.target.value })}
                                placeholder="Rider name *"
                                className="bg-white"
                                data-testid="walkin-rider-input"
                              />
                              <Input
                                value={walkInForm.horse_name}
                                onChange={(e) => setWalkInForm({ ...walkInForm, horse_name: e.target.value })}
                                placeholder="Horse name *"
                                className="bg-white"
                                data-testid="walkin-horse-input"
                              />
                              <Input
                                value={walkInForm.age_category}
                                onChange={(e) => setWalkInForm({ ...walkInForm, age_category: e.target.value })}
                                placeholder="Age category (optional)"
                                className="bg-white col-span-2"
                              />
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {eventClasses.map(cls => {
                                const selected = walkInForm.selected_classes.includes(cls);
                                return (
                                  <button
                                    type="button"
                                    key={cls}
                                    onClick={() => toggleWalkInClass(cls)}
                                    className={`text-xs px-2 py-1 rounded-full border ${selected ? 'bg-primary text-white border-primary' : 'bg-white text-stone-600 border-stone-300 hover:border-primary'}`}
                                  >
                                    {cls}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="flex gap-2 pt-1">
                              <Button type="button" variant="outline" onClick={() => { setShowAddWalkIn(false); setWalkInForm({ participant_name: '', horse_name: '', selected_classes: [], age_category: '' }); }}>
                                Cancel
                              </Button>
                              <Button type="button" onClick={submitWalkIn} className="bg-primary" data-testid="save-walkin-btn">
                                Add Walk-In
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Classes */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-lg font-semibold">Classes & Results</Label>
                <Button type="button" variant="outline" size="sm" onClick={addClass}>
                  <Plus className="w-4 h-4 mr-1" /> Add Class
                </Button>
              </div>

              {formData.classes.map((classItem, classIndex) => (
                <Card key={classIndex} className="border-stone-200">
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="grid grid-cols-2 gap-4 flex-1 mr-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Class Name *</Label>
                          <Input
                            value={classItem.class_name}
                            onChange={(e) => updateClass(classIndex, 'class_name', e.target.value)}
                            placeholder="e.g., Western Pleasure"
                            className="bg-stone-50"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Age Category (Optional)</Label>
                          <Input
                            value={classItem.age_category}
                            onChange={(e) => updateClass(classIndex, 'age_category', e.target.value)}
                            placeholder="e.g., Adult (18+)"
                            className="bg-stone-50"
                          />
                        </div>
                      </div>
                      {formData.classes.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeClass(classIndex)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    {/* Results for this class */}
                    <div className="space-y-2">
                      <Label className="text-sm text-stone-600">Placements</Label>
                      {classItem.results.map((result, resultIndex) => (
                        <div key={resultIndex} className="flex items-center gap-2 bg-stone-50 p-2 rounded-lg">
                          <div className="w-12 text-center font-bold text-primary">
                            #{result.place}
                          </div>
                          <Input
                            value={result.rider_name}
                            onChange={(e) => updateResult(classIndex, resultIndex, 'rider_name', e.target.value)}
                            placeholder="Rider name"
                            className="flex-1 bg-white"
                          />
                          <Input
                            value={result.horse_name}
                            onChange={(e) => updateResult(classIndex, resultIndex, 'horse_name', e.target.value)}
                            placeholder="Horse name"
                            className="flex-1 bg-white"
                          />
                          <Input
                            value={result.points}
                            onChange={(e) => updateResult(classIndex, resultIndex, 'points', e.target.value)}
                            placeholder="Points"
                            className="w-20 bg-white"
                            type="number"
                            step="0.1"
                          />
                          <Input
                            value={result.time}
                            onChange={(e) => updateResult(classIndex, resultIndex, 'time', e.target.value)}
                            placeholder="Time"
                            className="w-24 bg-white"
                            data-testid={`result-time-input-${classIndex}-${resultIndex}`}
                          />
                          {classItem.results.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeResult(classIndex, resultIndex)}
                              className="text-red-500 hover:text-red-700 px-2"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addResult(classIndex)}
                        className="w-full"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Placement
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes about this event..."
                className="bg-stone-50"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-primary">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingResult ? 'Update Results' : 'Save Results'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ResultsPage;
